import type { PreTradeTabType } from '@src/components/PreTrade/types';
import type { DealBookingType } from '@src/types/OTC';
import {
  abbreviateId,
  EMPTY_ARRAY,
  isMultileg,
  LedgerAccountTypeEnum,
  useGlobalDialog,
  useSecuritiesContext,
  type Security,
} from '@talos/kyoko';
import type { DockviewApi } from 'dockview';
import { maxBy } from 'lodash-es';
import type { MutableRefObject, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { v1 as uuid } from 'uuid';
import { setTemporaryPanel } from '../../components/AppLayout/AppLayoutSlice';
import { DEFAULT_LAYOUTS } from '../../components/AppLayout/Layouts';
import { DEFAULT_LAYOUT_ID } from '../../components/AppLayout/Layouts/tokens';
import { useLayoutSpecsQuery } from '../../components/AppLayout/LayoutSpecs/LayoutSpecSlice';
import { MAIN_GROUP_ID } from '../../components/Layout/tokens';
import type { AddPanelOptions } from '../../components/Layout/types';
import { getEnumeratedTitle } from '../../components/Layout/utils/utils';
import { MultilegComboType } from '../../components/MultilegCombo/enums';
import { getMultilegInstrumentType } from '../../components/OMS/NewOrder/utils/mappers';
import type { OrderDetailsPath, OrderRouteType } from '../../containers/Trading/Markets/OrderDetails/types';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import type { PanelComponentSpec, PanelConfig, PanelType } from '../../types/LayoutConfig';
import { useAppStateDispatch, useAppStateSelector } from '../AppStateProvider';
import { applyLayoutSpec } from './utils/applyLayoutSpec';
import { resetLayoutToDefault } from './utils/resetLayoutToDefault';

export interface AddDeepDivePanelParams {
  security: Security | undefined;
  marketAccounts: string[] | undefined;
  markets: string[] | undefined;
  aggregation: string | undefined;
}

export interface AddOptionsPanelParams {
  currency: string;
  market: string;
  marketAccount: string;
  underlyingCode?: string;
  expiry?: string;
}

export interface AddOrderDetailsPanelParams {
  orderID: string;
  tab: OrderDetailsPath | undefined;
  orderType: OrderRouteType | undefined;
}
export interface AddMultilegDeepDivePanelParams {
  security: Security | undefined;
  multilegInstrumentType: MultilegComboType;
  legMarketAccounts?: string[];
  legMarkets?: string[];
  legSymbols?: string[];
  isClone?: boolean;
}

export interface AddReconMismatchesDetailsPanelParams {
  mismatchIDs: string[];
}

export interface AddAccountLedgerEventsDetailsPanelParams {
  asset?: string;
  account: string;
  accountType: LedgerAccountTypeEnum;
}

export interface AppLayoutContextProps {
  addPanel: (panel: PanelComponentSpec, options: AddPanelOptions) => void;
  addPanelToMain: (
    panel: Pick<PanelComponentSpec, 'type' | 'label' | 'initialWidth' | 'initialHeight' | 'minWidth' | 'minHeight'>,
    options: Omit<AddPanelOptions, 'groupId' | 'panelId'>
  ) => void;
  dockviewApiRef: MutableRefObject<DockviewApi | undefined>;
  resetLayout: () => void;
  resetLayoutAndTabs: () => void;

  addTemporaryPanel: (panel: PanelType, label: string, params: PanelConfig) => void;

  addAutoHedgingRulesPanel: () => void;
  addOrderDetailsPanel: (params: AddOrderDetailsPanelParams) => void;
  addOptionsPanel: (params: AddOptionsPanelParams) => void;
  addManualRFQsPanel: () => void;
  addDeepDivePanel: (params: AddDeepDivePanelParams) => void;
  addMultilegDeepDivePanel: (params: AddMultilegDeepDivePanelParams) => void;
  addReconMismatchesDetailsPanel: (params: AddReconMismatchesDetailsPanelParams) => void;
  addAccountLedgerEventsDetailsPanel: (params: AddAccountLedgerEventsDetailsPanelParams) => void;
  addDealProductBuilderPanel: (params: { bookingType: DealBookingType; resolvedSymbol?: string }) => void;
  addTakerQuoteBreakdownPanel: (params: { rfqID: string }) => void;
  addManualRFQPricingPanel: (params: { rfqID: string }) => void;
  addPreTradePanel: (params: { sessionId: string; openTab?: PreTradeTabType; requestId?: string }) => void;
}

export const AppLayoutContext = createContext<AppLayoutContextProps | undefined>(undefined);
AppLayoutContext.displayName = 'AppLayoutContext';

export function useAppLayoutContext() {
  const context = useContext(AppLayoutContext);
  if (context === undefined) {
    throw new Error('Missing AppLayoutContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

/** Provide access to methods for modifying the layout for components outside the flexible part of the layout */
export function AppLayoutContextProvider({ children }: PropsWithChildren) {
  const dispatch = useAppStateDispatch();
  const temporaryPanel = useAppStateSelector(state => state.appLayout.temporaryPanel);
  const layoutSection = useAppStateSelector(state => state.appLayout.layoutSection);
  const selectedLayout = useAppStateSelector(state => state.appLayout.currentLayout);
  const { enableOTCSupport } = useFeatureFlag();
  const dockviewApiRef = useRef<DockviewApi | undefined>(undefined);
  const { searchableSecurities } = useSecuritiesContext();
  const { open: openGlobalDialog } = useGlobalDialog();

  const addPanel = useCallback(
    (
      panel: Pick<PanelComponentSpec, 'type' | 'label' | 'initialWidth' | 'initialHeight' | 'minWidth' | 'minHeight'>,
      { label, labelGetter, groupId, panelId, direction, params }: AddPanelOptions = {}
    ) => {
      const dockviewApi = dockviewApiRef.current;
      if (dockviewApi == null) {
        return;
      }
      const referenceGroup = groupId ? dockviewApi.getGroup(groupId) : undefined;
      const referencePanel = panelId ? dockviewApi.getPanel(panelId) : undefined;
      const id = uuid();
      const { defaultParams, defaultTitle } = getDefaultConfig(panel.type, searchableSecurities);
      const title = getEnumeratedTitle(label ?? labelGetter?.() ?? defaultTitle ?? panel.label, dockviewApi.panels);

      // Don't change the dimensions of the panel if it is added to an existing group
      const initialWidth = direction === 'left' || direction === 'right' ? panel.initialWidth : undefined;
      const initialHeight = direction === 'above' || direction === 'below' ? panel.initialHeight : undefined;

      const minimumWidth = panel.minWidth;
      const minimumHeight = panel.minHeight;

      const newPanelProps = {
        id,
        component: panel.type,
        title,
        initialWidth,
        initialHeight,
        minimumWidth,
        minimumHeight,
        params: {
          ...defaultParams,
          ...params,
          type: panel.type,
        },
        position: referenceGroup
          ? {
              referenceGroup: groupId,
              direction: direction ?? 'within',
            }
          : referencePanel
            ? {
                referencePanel: panelId,
                direction: direction ?? 'right',
              }
            : direction && direction !== 'within'
              ? {
                  direction,
                }
              : undefined,
      };
      return dockviewApi.addPanel(newPanelProps);
    },
    [searchableSecurities]
  );

  const addTemporaryPanel = useCallback(
    (type: PanelType, label: string, params: PanelConfig) => {
      dispatch(
        setTemporaryPanel({
          type,
          section: layoutSection,
          params,
          label,
        })
      );
    },
    [dispatch, layoutSection]
  );

  const addPanelToMain = useCallback(
    (
      panel: Pick<PanelComponentSpec, 'type' | 'label' | 'initialWidth' | 'initialHeight' | 'minWidth' | 'minHeight'>,
      options: Omit<AddPanelOptions, 'groupId' | 'panelId'>
    ) => {
      const dockviewApi = dockviewApiRef.current;
      const group =
        dockviewApi?.getGroup(MAIN_GROUP_ID) ?? maxBy(dockviewApi?.groups, group => group.width * group.height);
      addPanel(panel, { ...options, groupId: group?.api.id ?? MAIN_GROUP_ID, direction: 'within' });
    },
    [addPanel]
  );

  const addDeepDivePanel = useCallback(
    ({ security, marketAccounts, markets, aggregation }: AddDeepDivePanelParams) => {
      if (security == null) {
        return;
      }
      addTemporaryPanel('deepDive', security.DisplaySymbol, {
        type: 'deepDive',
        symbol: security.Symbol,
        aggregation,
        marketAccounts: marketAccounts ?? EMPTY_ARRAY,
        markets: markets ?? EMPTY_ARRAY,
        chartResolution: '1H',
        annotations: [],
        chartType: 'candlestick',
        showMarkets: false,
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel]
  );

  const addMultilegDeepDivePanel = useCallback(
    ({
      security,
      multilegInstrumentType,
      legMarketAccounts,
      legMarkets,
      legSymbols,
      isClone,
    }: AddMultilegDeepDivePanelParams) => {
      const defaultInstrumentType = multilegInstrumentType ?? MultilegComboType.SyntheticCross;
      const multilegDetails = security?.MultilegDetails;
      const legParams = multilegDetails?.Parameters?.LegParams;

      const title =
        security == null || isClone
          ? 'New Multileg'
          : legSymbols && legSymbols.length > 0
            ? `New Multileg (${legSymbols[0]})`
            : security.DisplaySymbol;
      const instrumentType = multilegDetails
        ? (getMultilegInstrumentType(multilegDetails.SyntheticProductType) ?? defaultInstrumentType)
        : defaultInstrumentType;
      const initiatingLegs = legParams?.map(leg => leg.Initiating) ?? [];
      const haircutRatios = legParams?.map(leg => String(leg.HaircutRatio ?? '0')) ?? [];

      const resolvedSymbol = isClone && security ? undefined : security?.Symbol;

      addTemporaryPanel('multilegDeepDive', title, {
        type: 'multilegDeepDive',
        resolvedSymbol,
        instrumentType,
        initiatingLegs,
        haircutRatios,
        compactOrderBook: false,
        legSymbols: (isClone && security ? [security.Symbol] : legSymbols) ?? [],
        legMarketAccounts: legMarketAccounts ?? [],
        legMarkets: legMarkets ?? [],
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel]
  );

  const addOrderDetailsPanel = useCallback(
    ({ orderID, tab = 'details', orderType = 'order' }: AddOrderDetailsPanelParams) => {
      const type = 'orderDetails';
      addTemporaryPanel(type, `#${abbreviateId(orderID)}`, {
        type: type,
        orderID,
        orderType,
        openTab: tab,
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel]
  );

  const addOptionsPanel = useCallback(
    ({ currency, market, underlyingCode, expiry, marketAccount }: AddOptionsPanelParams) => {
      const type = 'options';
      addTemporaryPanel(type, `${currency} Options`, {
        type,
        optionFilter: {
          currency,
          market,
          underlyingCode,
          marketAccountName: marketAccount,
          expiry: expiry ? decodeURIComponent(expiry) : null,
        },
        strikeFilter: {},
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel]
  );

  const addReconMismatchesDetailsPanel = useCallback(
    ({ mismatchIDs }: AddReconMismatchesDetailsPanelParams) => {
      const type = 'reconMismatchesDetails';
      addTemporaryPanel(type, 'Recon Details', { type, mismatchIDs, closable: true, editable: true });
    },
    [addTemporaryPanel]
  );

  const addAccountLedgerEventsDetailsPanel = useCallback(
    ({ asset, account, accountType }: AddAccountLedgerEventsDetailsPanelParams) => {
      const type = 'accountLedgerEvents';
      addTemporaryPanel(type, 'Ledger Details', {
        type,
        asset,
        account,
        accountType,
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel]
  );

  const addDealProductBuilderPanel = useCallback(
    ({ bookingType, resolvedSymbol }: { bookingType: DealBookingType; resolvedSymbol?: string }) => {
      if (!enableOTCSupport) {
        return;
      }

      const type = 'dealProductBuilder';
      addTemporaryPanel(type, 'Deal Product Builder', {
        type,
        bookingType,
        resolvedSymbol,
        closable: true,
        editable: true,
      });
    },
    [addTemporaryPanel, enableOTCSupport]
  );

  const addManualRFQsPanel = useCallback(() => {
    addTemporaryPanel('manualRfqs', 'Manual RFQs', {
      type: 'manualRfqs',
      closable: true,
      editable: true,
      showAllDecimals: false,
    });
  }, [addTemporaryPanel]);

  const addTakerQuoteBreakdownPanel = useCallback(
    (params: { rfqID: string }) => {
      addTemporaryPanel('takerQuoteBreakdown', 'Quote Breakdown', {
        type: 'takerQuoteBreakdown',
        closable: true,
        editable: true,
        rfqID: params.rfqID,
      });
    },
    [addTemporaryPanel]
  );

  const addManualRFQPricingPanel = useCallback(
    (params: { rfqID: string }) => {
      addTemporaryPanel('manualRFQPricing', 'RFQ Pricing', {
        type: 'manualRFQPricing',
        closable: true,
        editable: true,
        rfqID: params.rfqID,
      });
    },
    [addTemporaryPanel]
  );

  const addPreTradePanel = useCallback(
    ({ sessionId, openTab, requestId }: { sessionId: string; openTab?: PreTradeTabType; requestId?: string }) => {
      addTemporaryPanel('preTrade', 'Pre-Trade', {
        type: 'preTrade',
        sessionId,
        openTab,
        requestId,
        closable: true,
        editable: false,
      });
    },
    [addTemporaryPanel]
  );

  const addAutoHedgingRulesPanel = useCallback(() => {
    const type = 'positionAutoHedgingRules';
    addTemporaryPanel(type, 'Position Auto Hedging Rules', { type, closable: true, editable: true });
  }, [addTemporaryPanel]);

  // Select all available layout spec templates
  const { data: sharedLayoutSpecs } = useLayoutSpecsQuery();

  /** Resets the layout to the template layout or the default layout if no template is found */
  const performLayoutReset = useCallback(
    (useDefaultTemplate: boolean) => {
      const dockviewApi = dockviewApiRef.current;
      if (dockviewApi == null) {
        return;
      }
      const currentLayout = dockviewApi.toJSON();
      const templateId = useDefaultTemplate ? DEFAULT_LAYOUT_ID : (selectedLayout?.templateId ?? selectedLayout?.id);
      const sharedLayoutSpec = useDefaultTemplate
        ? undefined
        : sharedLayoutSpecs?.find(spec => spec.Config.id === selectedLayout?.templateId)?.Config.layoutSpec;
      const layoutSpec = structuredClone(
        sharedLayoutSpec ?? DEFAULT_LAYOUTS[layoutSection][templateId ?? DEFAULT_LAYOUT_ID]
      );
      const defaultLayout = layoutSpec.dockViewLayout;
      const dockViewLayout = resetLayoutToDefault(currentLayout, defaultLayout);
      const newLayoutSpec = { ...layoutSpec, dockViewLayout: structuredClone(dockViewLayout) };
      applyLayoutSpec(dockviewApi, newLayoutSpec);
    },
    [layoutSection, selectedLayout, sharedLayoutSpecs]
  );

  /** Move all additional panels to the main group and reset layout */
  const resetLayout = useCallback(() => {
    const dockviewApi = dockviewApiRef.current;
    if (dockviewApi == null) {
      return;
    }

    // Check if template exists
    const templateId = selectedLayout?.templateId;
    if (templateId != null) {
      const sharedLayoutSpec = sharedLayoutSpecs?.find(spec => spec.Config.id === templateId)?.Config.layoutSpec;
      const defaultLayoutSpec = DEFAULT_LAYOUTS[layoutSection][templateId];

      // If template is not found in either location, show warning dialog
      if (sharedLayoutSpec == null && defaultLayoutSpec == null) {
        openGlobalDialog({
          title: 'Template Not Found',
          content:
            'The template for this layout is no longer available. Would you like to reset to the default layout?',
          confirmLabel: 'Reset to Default',
          cancelLabel: 'Cancel',
          width: 320,
          onConfirm: () => performLayoutReset(true),
        });
        return;
      }
    }

    // Template exists or no templateId, proceed normally
    performLayoutReset(false);
  }, [layoutSection, selectedLayout, sharedLayoutSpecs, openGlobalDialog, performLayoutReset]);

  const resetLayoutAndTabs = useCallback(() => {
    const templateId = selectedLayout?.templateId ?? selectedLayout?.id;
    const sharedLayoutSpec = sharedLayoutSpecs?.find(spec => spec.Config.id === selectedLayout?.templateId)?.Config
      .layoutSpec;
    const layoutSpec = structuredClone(
      sharedLayoutSpec ?? DEFAULT_LAYOUTS[layoutSection][templateId ?? DEFAULT_LAYOUT_ID]
    );
    const dockviewApi = dockviewApiRef.current;
    if (dockviewApi) {
      applyLayoutSpec(dockviewApi, layoutSpec);
    }
  }, [layoutSection, selectedLayout, sharedLayoutSpecs]);

  const value = useMemo(
    () => ({
      dockviewApiRef,
      temporaryPanel,
      addTemporaryPanel,
      addPanel,
      addPanelToMain,
      addDeepDivePanel,
      addMultilegDeepDivePanel,
      addAutoHedgingRulesPanel,
      addReconMismatchesDetailsPanel,
      addAccountLedgerEventsDetailsPanel,
      addDealProductBuilderPanel,
      addOrderDetailsPanel,
      addOptionsPanel,
      addManualRFQsPanel,
      addTakerQuoteBreakdownPanel,
      addManualRFQPricingPanel,
      addPreTradePanel,
      resetLayout,
      resetLayoutAndTabs,
    }),
    [
      dockviewApiRef,
      temporaryPanel,
      addTemporaryPanel,
      addPanel,
      addPanelToMain,
      addManualRFQsPanel,
      addDeepDivePanel,
      addMultilegDeepDivePanel,
      addAutoHedgingRulesPanel,
      addReconMismatchesDetailsPanel,
      addAccountLedgerEventsDetailsPanel,
      addDealProductBuilderPanel,
      addOrderDetailsPanel,
      addOptionsPanel,
      addTakerQuoteBreakdownPanel,
      addManualRFQPricingPanel,
      addPreTradePanel,
      resetLayout,
      resetLayoutAndTabs,
    ]
  );

  return <AppLayoutContext.Provider value={value}>{children}</AppLayoutContext.Provider>;
}

function getDefaultConfig(
  panelType: PanelType,
  securities: Security[]
): { defaultParams: Partial<PanelConfig>; defaultTitle?: string } {
  const nonMultilegSecurities = securities.filter(security => !isMultileg(security));
  switch (panelType) {
    case 'marketCards': {
      return {
        defaultParams: {},
        defaultTitle: 'Market',
      };
    }
    case 'priceLadder':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} prices`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'volumeLadder':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} volume`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'tradesHistory':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} trades`,
        defaultParams: {
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
        },
      };
    case 'deepDive':
      return {
        defaultTitle: nonMultilegSecurities[0]?.DisplaySymbol,
        defaultParams: {
          type: 'deepDive',
          symbol: nonMultilegSecurities[0]?.Symbol,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          chartResolution: '1H',
          annotations: [],
          chartType: 'candlestick',
          showMarkets: false,
          closable: true,
          editable: true,
        },
      };
    case 'orderBook':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} Book`,
        defaultParams: {
          type: 'orderBook',
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          showCumQty: true,
          closable: true,
          editable: true,
        },
      };
    case 'historicalPriceChart':
      return {
        defaultTitle: `${nonMultilegSecurities[0]?.DisplaySymbol} Chart`,
        defaultParams: {
          type: 'historicalPriceChart',
          symbol: nonMultilegSecurities[0]?.Symbol,
          settingsInitialOpen: true,
          marketAccounts: EMPTY_ARRAY,
          markets: EMPTY_ARRAY,
          chartResolution: '1H',
          annotations: [],
          chartType: 'candlestick',
          closable: true,
          editable: true,
        },
      };
    case 'multilegDeepDive':
      return {
        defaultParams: {
          type: 'multilegDeepDive',
          resolvedSymbol: undefined,
          instrumentType: MultilegComboType.SyntheticCross,
          initiatingLegs: [],
          compactOrderBook: false,
          legSymbols: [],
          legMarketAccounts: [],
          legMarkets: [],
          closable: true,
          editable: true,
        },
      };
    case 'options':
      return {
        defaultParams: {
          type: 'options',
          closable: true,
          editable: true,
        },
      };
    case 'reconMismatchesDetails':
      return {
        defaultParams: {
          type: 'reconMismatchesDetails',
          mismatchIDs: [],
          closable: true,
          editable: true,
        },
      };
    case 'accountLedgerEvents':
      return {
        defaultParams: {
          type: 'accountLedgerEvents',
          asset: undefined,
          account: '',
          accountType: LedgerAccountTypeEnum.MarketAccount,
          closable: true,
          editable: true,
        },
      };
    case 'positionAutoHedgingRules':
      return {
        defaultParams: {
          type: 'positionAutoHedgingRules',
          closable: true,
          editable: true,
        },
      };
    case 'dealProductBuilder':
      return {
        defaultParams: {
          type: 'dealProductBuilder',
          bookingType: 'forward',
          closable: true,
          editable: true,
        },
      };
    default:
      return { defaultParams: {} };
  }
}
